/**
 * Curated image pools per category + a guaranteed fallback.
 *
 * Photo IDs are Unsplash IDs already in use across the live app (so they load).
 * `galleryFor` returns N distinct URLs for a product so every listing has a
 * front / back / side style gallery. `placeholder` returns an inline SVG data
 * URI that ALWAYS renders, used as the final onError fallback so no tile is
 * ever blank.
 */

const img = (id: string, w: number, h: number) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&h=${h}&fit=crop&auto=format&q=80`;

// Only IDs already used (and rendering) across the original app - so every
// product resolves to a real photo. Any miss still falls back to placeholder().
export const IMAGE_POOLS: Record<string, string[]> = {
  womenswear: [
    '1490481651871-ab68de25d43d', '1539533018447-63fcce2678e3', '1603252109303-2751441dd157',
    '1434389677669-e08b4cac3105', '1576566588028-4147f3842f27', '1539109136881-3be0616acf4b',
  ],
  menswear: [
    '1521572163474-6864f9cf17ab', '1591047139829-d91aecb6caea', '1507680434567-5739c80be1ac',
    '1594938298603-c8148c4dae35', '1473966968600-fa801b869a1a',
  ],
  kids: [
    '1503944168849-8bf86cb31b6c', '1434389677669-e08b4cac3105', '1591047139829-d91aecb6caea',
  ],
  traditional: [
    '1583744026327-f8b2a4e3e462', '1610030033233-49ed30e6f9ce', '1724856604254-f7cf4e9c8f72',
    '1675387404357-096f658a53f0', '1708170236215-b6edcad7f49a', '1696962678565-bee84e6b9cb6',
    '1625646741211-711bdd65c570', '1530884698386-d42ad3199b1f', '1708170236295-20ab8fbadcef',
    '1485570661444-73b3f0ff9d2f', '1767955694884-d4bf352c23c2', '1746372283841-dbb3838f9935',
    '1711130388758-2ccf44bb735c',
  ],
  shoes: [
    '1600185365483-26d7a4cc7519', '1491553895911-0055eca6402d', '1549298916-b41d501d3772',
    '1525966222134-fcfa99b8ae77',
  ],
  bags: [
    '1548036328-c9fa89d128fa', '1559563458-527698bf5295', '1548863227-3af567fc3b27',
    '1584917865442-de89df76afd3',
  ],
  accessories: [
    '1524592094714-0f0654e20314', '1596462502278-27bfdc403348', '1611591437281-460bfbe1220a',
    '1515562141207-7a88fb7ce338', '1605100804763-247f67b3557e', '1588444837495-c6cfeb53f32d',
    '1535556116002-6281ff3e9f36',
  ],
  watches: ['1524592094714-0f0654e20314', '1507680434567-5739c80be1ac'],
  electronics: ['1511707171634-5f897ff02aa9', '1510557880182-3d4d3cba35a5', '1496181133206-80ce9b88a853'],
  fragrance: ['1541643600914-78b084683601', '1596462502278-27bfdc403348'],
};

const FALLBACK_POOL = IMAGE_POOLS.womenswear;

function poolFor(category: string): string[] {
  return IMAGE_POOLS[category]?.length ? IMAGE_POOLS[category] : FALLBACK_POOL;
}

/** N distinct gallery URLs (front/back/side …) for a product. */
export function galleryFor(category: string, seed: number, n = 3, size = 900): string[] {
  const pool = poolFor(category);
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    out.push(img(pool[(seed + i) % pool.length], size, size));
  }
  return out;
}

// Garment type (from the catalogue helper) → the most fitting image pool, so a
// watch shows watches and a bag shows bags even when the listing's broad
// category is just "womenswear" / "menswear".
const GARMENT_POOL_KEY: Record<string, string> = {
  shoes: 'shoes', bag: 'bags', watch: 'watches',
  phone: 'electronics', laptop: 'electronics',
  accessory: 'accessories', traditional: 'traditional',
};

/** A stable numeric seed from a product id so its gallery is deterministic. */
export function seedFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 997;
}

/**
 * N distinct, category-correct gallery URLs for a product, chosen by garment
 * type first and falling back to the listing category. Used to guarantee every
 * product detail page has a full front / back / side / detail set.
 */
export function galleryForGarment(garment: string, category: string, seed: number, n = 5, size = 900): string[] {
  const key = GARMENT_POOL_KEY[garment] ?? (IMAGE_POOLS[category]?.length ? category : 'womenswear');
  const pool = IMAGE_POOLS[key]?.length ? IMAGE_POOLS[key] : FALLBACK_POOL;
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(img(pool[(seed + i) % pool.length], size, size));
  return out;
}

/** Primary cover image (portrait) for a product. */
export function coverFor(category: string, seed: number): string {
  const pool = poolFor(category);
  return img(pool[seed % pool.length], 600, 800);
}

const PALETTE: Record<string, string> = {
  womenswear: '#EADDC8', menswear: '#E4D7BE', kids: '#EBE0CB', traditional: '#E8D3B0',
  shoes: '#E3D6BE', bags: '#E7DAC0', accessories: '#EDE2CC', watches: '#E2D4B8',
  electronics: '#E6DAC4', fragrance: '#EEE3CF',
}

/**
 * Inline SVG placeholder - always renders, so a broken image URL never leaves
 * a blank tile. Shows the brand + category on a branded background.
 */
export function placeholder(label: string, category = ''): string {
  const bg = PALETTE[category] ?? '#0F0F12';
  const safe = (label || 'Souqli').slice(0, 22).replace(/[<&>]/g, '');
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='800'>
    <rect width='600' height='800' fill='${bg}'/>
    <rect width='600' height='800' fill='url(#g)' opacity='0.6'/>
    <defs><radialGradient id='g' cx='50%' cy='30%' r='75%'><stop offset='0%' stop-color='#F7EBD8'/><stop offset='100%' stop-color='${bg}'/></radialGradient></defs>
    <circle cx='300' cy='322' r='72' fill='none' stroke='#D98A2B' stroke-width='2' opacity='0.55'/>
    <text x='300' y='346' fill='#A66018' font-family='Georgia, serif' font-size='68' font-style='italic' text-anchor='middle'>S</text>
    <text x='300' y='452' fill='#221A12' font-family='Georgia, serif' font-size='27' text-anchor='middle'>${safe}</text>
    <text x='300' y='488' fill='#8A7A66' font-family='monospace' font-size='12' letter-spacing='4' text-anchor='middle'>${category.toUpperCase()}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
